import { formatDate } from "./page";
import { calculateCarbonEmission } from "./page";
import ResponsiveCDNImage from "../../components/common/image/responsive-cdn-image";
import { track } from "@amplitude/analytics-browser";
import Partition from "../../components/common/partition";
import { useNavigate } from "react-router-dom";
import { HiClock } from "react-icons/hi2";
import clsx from "clsx";
import { OrderStatus } from "../../components/order/order-status";
import { getOrderType } from "../../utils/order";

// 리뷰 작성하기 버튼
const ReviewButton = ({
  onClick,
  order,
}: {
  onClick: () => void;
  order: any;
}) => {
  const reviewWriteStatus = order?.reviewWriteStatus;

  switch (reviewWriteStatus) {
    case "기한만료":
      return (
        <button className="w-full p-2 border-2 border-gray-200 rounded-3xl flex items-center justify-center bg-gray-50">
          <span className="text-gray-500 font-semibold text-md">
            포토리뷰 스탬프 작성 기한이 지났어요
          </span>
        </button>
      );
    case "불가능":
      return null;
    case "대기":
      return (
        <button className="w-full p-2 border-2 border-primary/30 rounded-3xl flex items-center justify-center bg-white">
          <HiClock className="text-primary w-4 h-4 mr-1" />
          <span className="text-primary font-semibold text-md">
            픽업시간 이후 리뷰 작성이 가능해요
          </span>
        </button>
      );
    case "가능":
      return (
        <button
          onClick={onClick}
          className="w-full p-2 border rounded-3xl flex items-center justify-center bg-primary active:bg-primary/90 transition-colors"
        >
          <span className="text-white font-semibold text-md">
            리뷰 작성하기
          </span>
        </button>
      );
    case "완료":
      return (
        <button className="w-full p-2 border border-primary/30 rounded-3xl flex items-center justify-center bg-primary/10">
          <span className="text-primary font-semibold text-md">
            리뷰 작성완료
          </span>
        </button>
      );
    default:
      return null;
  }
};

interface OrderListProps {
  order: any[];
  isHorizontal?: boolean;
  isFromReviewComplete?: boolean;
}

const OrderList = ({
  order,
  isHorizontal = false,
  isFromReviewComplete = false,
}: OrderListProps) => {
  const navigate = useNavigate();

  return (
    <>
      {order?.map((orderItem) => {
        // 주문내역 이미지
        const orderImage =
          (orderItem?.products?.[0]?.img as string) ||
          (orderItem?.products?.[0]?.menus?.[0]?.img as string) ||
          (orderItem?.pack?.store?.img as string) ||
          "";

        return (
          <div
            key={orderItem?.id}
            className={clsx(isHorizontal ? "flex-shrink-0 w-fit" : "")}
          >
            <div
              className={clsx(
                "w-full bg-white flex flex-col",
                isHorizontal ? "border rounded-2xl shadow-md p-3" : "p-4"
              )}
            >
              <div className="text-md">
                <span className="text-gray-700">
                  {formatDate(orderItem?.createdAt)}{" "}
                </span>
                <span className="text-gray-700">· </span>
                <OrderStatus order={orderItem} />
              </div>

              <div className="w-full flex justify-start gap-2 mt-2">
                <div className="h-full flex justify-end items-end">
                  <ResponsiveCDNImage
                    className={clsx(
                      "rounded-md object-cover border border-gray-200",
                      isHorizontal ? "h-[50px] w-[50px]" : "h-[90px] w-[90px]"
                    )}
                    src={orderImage}
                    alt="팩 사진"
                    sizeRatio={1 / 4}
                  />
                </div>

                <div className="flex flex-col justify-between gap-1">
                  <div>
                    <div>
                      <span className="text-black text-lg font-semibold">
                        {orderItem?.products?.[0]?.store.title ||
                          orderItem?.pack?.store?.title}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <span className="text-gray-700 text-md font-medium">
                        {getOrderType(orderItem)}
                      </span>
                      <span className="text-gray-700 text-md font-medium">
                        {orderItem?.amount?.toLocaleString()}원
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <div className="flex">
                      <div className="px-1.5 py-1.5 bg-[#f3f3f3] rounded-md flex items-center">
                        <div className="text-black text-[10px]">
                          {orderItem?.totalDiscount?.toLocaleString()}원
                          할인받았어요
                        </div>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="px-1.5 py-1.5 bg-[#edfbdf] rounded-md flex items-center break-keep">
                        <div className="text-gray-700 text-[10px] font-medium">
                          <span>CO</span>
                          <span className="uppercase">
                            <sub>2</sub>
                          </span>
                          를
                          <span className="ml-1">
                            {calculateCarbonEmission(orderItem?.amount)}kg
                            줄였어요!
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-3">
                <button
                  className="w-full p-2 border rounded-3xl flex items-center justify-center"
                  onClick={() => {
                    if (isFromReviewComplete) {
                      track("리뷰작성완료 페이지 상세보기 클릭");
                    } else {
                      track("주문내역 상세보기 클릭");
                    }
                    navigate(`/order/${orderItem?.id}`);
                  }}
                >
                  <span className="text-gray-600 text-md font-medium">
                    상세보기
                  </span>
                </button>
                <ReviewButton
                  onClick={() => {
                    if (isFromReviewComplete) {
                      track("리뷰작성완료 페이지 리뷰 작성하기 클릭");
                    } else {
                      track("주문내역 리뷰 작성하기 클릭");
                    }
                    navigate(`/order/${orderItem?.id}/review`);
                  }}
                  order={orderItem}
                />
              </div>
            </div>
            {!isHorizontal && <Partition color="light" height="thick" />}
          </div>
        );
      })}
    </>
  );
};

export default OrderList;
